import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "@/components/PageHead";
import { POLL } from "@/lib/format";
import { useEffect, useState, type ReactNode } from "react";
import { useCompanyScope } from "@/auth/companyScope";

import { fota, type Batch, type BatchDevice, type FotaDevice } from "@/api/client";
import { Chip, type ChipTone } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { MultiSelect } from "@/components/MultiSelect";
import { Select, SearchInput } from "@/components/inputs";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { useConfirm } from "@/components/ConfirmDialog";
import { Field } from "@/components/Field";
import { kindLabel, useModelPool, useTemplates } from "@/modules/asset_catalog/common";
import { CollapsibleSection } from "@/components/Section";
import { Donut } from "@/components/Donut";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
// ONE vocabulary for rollout states: the chips, the breakdown, and the donut all
// read label AND color from this map — identical color per state everywhere.
// SENT = command published, device online, no ack yet. NOTIFIED = device ACKED
// (st1) — publishing into the broker is not notification of anything. OFFLINE =
// pre-receipt and dark (the online-nudge re-sends the moment it heartbeats).
const STATE_CHIP: StatusMap = {
  pending: { label: "PENDING", color: "#8B95A3" },
  sent: { label: "SENT", color: "#5C7CFA" },
  offline: { label: "OFFLINE", color: "var(--orange)" },
  notified: { label: "NOTIFIED", color: "var(--teal)" },
  downloading: { label: "DOWNLOADING", color: "var(--info)" },
  staged: { label: "STAGED", color: "var(--warn)" },
  apply_deferred: { label: "APPLY DEFERRED", color: "#9775FA" },
  flashing: { label: "FLASHING", color: "var(--teal-deep)" },
  interim_error: { label: "INTERIM ERROR", color: "#E64980" },  // errored, retrying (max 3 episodes)
  success: { label: "SUCCESS", color: "var(--ok)" },
  failed: { label: "FAILED", color: "var(--bad)" },
};
const BATCH_CHIP: Record<string, ChipTone> = { active: "ok", stopped: "warn", done: "mute", draft: "info" };
// rollout lifecycle order, for the donut + per-state device breakdown
const STATE_ORDER = [
  "pending", "sent", "offline", "notified", "downloading", "staged", "apply_deferred", "flashing",
  "interim_error", "success", "failed",
];



/** Version multi-select — thin wrapper over the shared MultiSelect (U9). */
/** The non-host controllers a board reports, in one cell: `BLE 203 · MDM 12`.
 *
 *  ONE COLUMN RATHER THAN TWO, because the picker is already nine columns wide
 *  inside a 240 px scroller and these two are read together — "what else is on
 *  this board" is one question. The dash is returned for a board that reports
 *  no planes at all, which is every non-connected product, so it must read as
 *  "none reported" and not as "zero".
 */
function planeSummary(pv: FotaDevice["plane_vers"]): ReactNode {
  const parts = [
    pv?.ble?.sw != null ? `BLE ${pv.ble.sw}` : null,
    pv?.modem_cell?.sw != null ? `MDM ${pv.modem_cell.sw}` : null,
  ].filter(Boolean);
  return parts.length
    ? parts.join(" · ")
    : <span style={{ color: "var(--t-dim)" }}>—</span>;
}

function VerMultiSelect({ options, value, onChange }: {
  options: number[]; value: number[]; onChange: (v: number[]) => void;
}) {
  return (
    <MultiSelect<number>
      options={options.map((v) => ({ value: v, label: `v${v}` }))}
      value={value}
      onChange={onChange}
      emptyLabel="All versions"
      noneLabel="none seen yet"
      clearLabel="Clear - all versions"
    />
  );
}

export function FotaUpdatePage() {
  const qc = useQueryClient();
  const { ask, dialog } = useConfirm();
  const fw = useQuery({ queryKey: ["fota", "fw"], queryFn: fota.firmware });
  const batches = useQuery({ queryKey: ["fota", "batches"], queryFn: fota.batches, refetchInterval: POLL.STATE });
  // targeting flow (mirrors the Asset Data picker): Company → Asset type →
  // Subcomponent (kind → model) → Software & method — the method options are
  // exactly the SELECTED model's catalog FOTA ticks ("kind decides").
  const tpl = useTemplates();
  const pool = useModelPool();
  // THE OWNER COMES FROM THE APP BAR (D-134). "" means the bar is at ALL
  // companies, and this page treats that as nothing targeted -- see below.
  const { company: fCompany } = useCompanyScope();
  // LIFE STAGE (D-83), the other half of the picker that replaced the
  // allocated/unallocated buckets. Derived server-side from the bound part.
  const [fState, setFState] = useState("");
  const [fAssetType, setFAssetType] = useState("");
  const [fKind, setFKind] = useState("");
  const [fModelId, setFModelId] = useState("");
  const [fMethod, setFMethod] = useState("");
  const [fBl, setFBl] = useState<number[]>([]);
  const [fApp, setFApp] = useState<number[]>([]);

  const fotaModels = (pool.data?.models ?? []).filter((m) => m.status === "active"
    && Array.isArray((m.derived as { fota_methods?: unknown[] }).fota_methods)
    && ((m.derived as { fota_methods?: unknown[] }).fota_methods?.length ?? 0) > 0);
  const fotaKinds = [...new Set(fotaModels.map((m) => m.kind))];
  const kindModels = fotaModels.filter((m) => !fKind || m.kind === fKind);
  const model = fotaModels.find((m) => m.version_id === fModelId);
  type MethodTick = { key: string; software: string; route: string; path?: string;
                      // the SERVER's answer to "can Orbit drive this line", derived
                      // from the executor registry — never decided in this file
                      supported_now?: boolean };
  const methods: MethodTick[] = ((model?.derived as { fota_methods?: MethodTick[] })?.fota_methods) ?? [];
  const curMethod = methods.find((m) => m.key === fMethod);
  const METHOD_SW: Record<string, string> = { fota_host_cell: "host", fota_host_ble: "host",
    fota_modem_cell: "modem_cell", fota_ble_cell: "ble", fota_ble_phone: "ble" };
  const selSoftware = METHOD_SW[fMethod] ?? "host";

  /** NODE then SOFTWARE, the D-123 shape the upload form already uses.
   *
   *  Dattatray: *"After pint 5 we shoudl now have selection of what under host
   *  and Ble we want to udpate / Node and Softare inside that node
   *  selection!"*
   *
   *  A ROLLOUT IS NARROWER THAN AN UPLOAD, and that is the whole difference
   *  between these two forms. Upload offers everything the CATALOGUE declares,
   *  because a bootloader is a real artefact that reaches a bench through a
   *  tool. A rollout may only offer what Orbit can DRIVE over the air: a
   *  bootloader has no OTA executor by nature (`legacy_software_for` returns
   *  None for it, so `executor_for_software` raises), and a config is never
   *  flashed at all.
   *
   *  SO THE OPTIONS COME FROM `fota_methods`, NOT FROM `sw_components`. Each
   *  method already carries `supported_now`, the SERVER's answer from the
   *  executor registry -- the same value this file has deliberately never
   *  second-guessed. Every legacy method is an `app` artefact on a FOTA route
   *  (templates.py says so where the mapping lives), which is why the Software
   *  picker offers Application SW today and nothing else: those are the only
   *  (node, artefact) pairs with an engine behind them.
   *
   *  A node whose only method is UNDRIVABLE is still listed, greyed, with the
   *  reason -- the pattern `supported_now` was added for. Hiding it would make
   *  a model's own catalog tick vanish with no explanation.
   */
  const NODE_LABEL: Record<string, string> = {
    host: "Host controller", ble: "BLE module", modem_cell: "Telematics modem" };
  const NODE_ORDER = ["host", "ble", "modem_cell"];
  const nodeMethods = (n: string) => methods.filter((m) => METHOD_SW[m.key] === n);
  const nodes = NODE_ORDER.filter((n) => nodeMethods(n).length > 0);
  const fNode = METHOD_SW[fMethod] ?? "";
  // Choosing a node selects the best METHOD for it -- drivable first -- so the
  // request shape and `methodReady` are unchanged by this presentation.
  const pickNode = (n: string) => {
    const mm = nodeMethods(n);
    setFMethod((mm.find((x) => x.supported_now) ?? mm[0])?.key ?? "");
    setFwId(""); setSelIds([]);
  };
  // THE SERVER SAYS WHAT IT CAN DRIVE. This used to compare the method key
  // against a literal here — a second hardcoded list beside the server's own
  // one, so registering the BLE executor enabled nothing and the Create
  // button stayed disabled with a message about a release that had already
  // shipped. `supported_now` is derived from the executor registry, so a new
  // method needs no change in this file.
  const methodReady = !!curMethod?.supported_now;
  const pickModel = (id: string) => {
    setFModelId(id); setFwId(""); setSelIds([]);
    const mm = ((fotaModels.find((m) => m.version_id === id)?.derived as { fota_methods?: MethodTick[] })
      ?.fota_methods) ?? [];
    // Prefer a method Orbit can actually DRIVE over the model's first tick,
    // so picking a model does not land on a method whose Create button is
    // disabled. Which ones are drivable is the server's answer, not a list
    // kept here.
    setFMethod((mm.find((x) => x.supported_now) ?? mm[0])?.key ?? "");
  };

  // NO OWNER, NO CANDIDATES. Dattatray: *"if company is not selected how can
  // we have list of nodes?"* -- and he was looking at a page that said
  // "Select a company..." above a list of five devices and a live
  // "Create rollout (5)" button. Changing the label without changing the
  // behaviour made the control LIE; the old "All companies" was at least
  // true. For a firmware push the unselected state has to mean nothing is
  // targeted, because the alternative default is every customer at once.
  const devices = useQuery({
    // NOT `enabled: !!fCompany`, AND THAT WAS A DEADLOCK I SHIPPED. The
    // company list lives in THIS response (`meta.companies`), so gating the
    // query on a company meant: no company -> no request -> no meta -> an
    // EMPTY owner dropdown -> no way to ever choose one. The fix for "no
    // owner, no candidates" had removed the ability to pick an owner, and it
    // took the boot/app version options with it.
    //
    // The options and the candidates arrive together but are not the same
    // thing, so the gate belongs on the CANDIDATES, below.
    queryKey: ["fota", "devices", fCompany, fState, fAssetType, fModelId, fMethod, fBl.join(","), fApp.join(",")],
    queryFn: () => fota.devices({
      company: fCompany || undefined,
      state: fState || undefined,
      asset_type: fAssetType || undefined,
      version_id: fModelId || undefined,
      method: fMethod || undefined,
      bl_ver: fBl.length ? fBl.join(",") : undefined,
      app_ver: fApp.length ? fApp.join(",") : undefined,
    }),
    refetchInterval: POLL.LIST,
  });
  const firmwares = fw.data?.firmware ?? [];
  const list = batches.data?.batches ?? [];
  // NO OWNER, NO CANDIDATES -- gated here rather than at the request, so the
  // filter options still load. For a firmware push the unselected state has
  // to target nothing: the alternative default is every customer at once.
  const knownDevices = fCompany ? (devices.data?.devices ?? []) : [];
  const total = fCompany ? (devices.data?.total ?? 0) : 0;   // full filtered count (may exceed the 1000 shown)
  // BUSY ELSEWHERE (0078). A device a live rollout still holds is excluded
  // from a new one, so `total` alone would promise more than the batch does.
  const heldElsewhere = devices.data?.meta?.held_elsewhere ?? 0;
  const targetable = Math.max(0, total - heldElsewhere);
  const meta = devices.data?.meta;


  // --- create rollout ---
  const [fwId, setFwId] = useState("");
  const [name, setName] = useState("");
  const [lifetimeH, setLifetimeH] = useState("24");
  const [selIds, setSelIds] = useState<string[]>([]);
  useEffect(() => { setSelIds([]); }, [fCompany]);
  const [createMsg, setCreateMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const create = useMutation({
    // Two shapes (1M law): ticked devices -> explicit list (bench/trial); nothing
    // ticked -> a SELECTOR from the current filters, snapshotted server-side by
    // one INSERT..SELECT — the device list never crosses the wire, so "all 1M
    // matching" costs the same request as 2 devices.
    mutationFn: () => fota.createBatch({
      firmware_id: fwId,
      name: name.trim() || "rollout",
      ...(selIds.length
        ? { connection_ids: selIds }
        : { selector: { company: fCompany || null, asset_type: fAssetType || null,
                        version_id: fModelId || null, method: fMethod || null,
                        bl_vers: fBl.length ? fBl : null,
                        app_vers: fApp.length ? fApp : null } }),
      wave_size: 0, // rollout driver default pacing (50 devices / 15 s tick)
      lifetime_hours: Number(lifetimeH) || undefined,
    }),
    onSuccess: (r) => {
      setName(""); setSelIds([]);
      setCreateMsg({ ok: true, text:
        `Draft created (${r.total} device${r.total === 1 ? "" : "s"}) - press Start below to send.` });
      qc.invalidateQueries({ queryKey: ["fota", "batches"] });
    },
    onError: (e) => setCreateMsg({ ok: false, text: `Create failed: ${String(e)}` }),
  });

  const start = useMutation({ mutationFn: (id: string) => fota.startBatch(id), onSuccess: () => qc.invalidateQueries({ queryKey: ["fota", "batches"] }) });
  const cancel = useMutation({ mutationFn: (id: string) => fota.cancelBatch(id), onSuccess: () => qc.invalidateQueries({ queryKey: ["fota", "batches"] }) });
  const archive = useMutation({ mutationFn: (id: string) => fota.archiveBatch(id), onSuccess: () => qc.invalidateQueries({ queryKey: ["fota", "batches"] }) });
  const del = useMutation({ mutationFn: (id: string) => fota.deleteBatch(id), onSuccess: () => qc.invalidateQueries({ queryKey: ["fota", "batches"] }) });
  const activeList = list.filter((b) => !b.archived_at);
  const archivedList = list.filter((b) => b.archived_at);

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Update" title="Batch update" />

      {/* New rollout - the journey: WHO (company) → WHAT (target firmware) →
          NARROW (current boot/app versions) → PICK devices → NAME & create */}
      <div className="section-label">New rollout</div>
      <div className="panel panel-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* TWO QUESTIONS, ONE LEVEL EACH (D-83). This was four entries mixing
              two levels of one hierarchy"All (allocated + unallocated)",
              "All allocated", "Unallocated", and then the companies - so
              picking between them meant silently knowing which level you were
              on. "All allocated" was a BUCKET and "Aphelion Energy" a MEMBER of
              it, presented as peers.
              Now every enrolled device has an owner, so the buckets are gone:
              WHOSE is a company, and WHAT STAGE is the life stage. */}
          {/* NO OWNER CONTROL HERE (D-134): the company is the app bar's.
              The safety property it carried survives. Dattatray: *"the
              company dropdown shall start with select company and not all
              companies."* A rollout at "All companies" means the default
              target of a FLEET-WIDE firmware push is every customer at once,
              so with the bar at ALL this page lists nothing and says so. */}
          {/* Derived from the bound part - a connection has no stage of its
              own, exactly as it has no owner of its own (§10). */}
          <Field label="1 · Stage">
            <Select className="fill" value={fState}
              onChange={(e) => { setFState(e.target.value); setSelIds([]); }}>
              <option value="">Any stage</option>
              <option value="inventory">Inventory - on a shelf</option>
              <option value="deployed">Deployed - in service</option>
              <option value="repair">Repair</option>
              <option value="scrapped">Scrapped</option>
            </Select>
          </Field>
          <Field label="2 · Asset type">
            <Select className="fill" value={fAssetType}
              onChange={(e) => { setFAssetType(e.target.value); setSelIds([]); }}>
              <option value="">Any (installed or not)</option>
              <option value="vehicle">Vehicle - installed in a vehicle</option>
              <option value="battery">Battery - installed in a battery</option>
            </Select>
          </Field>
          <Field label="3 · Subcomponent (kind → model, from the Asset Catalog)">
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr)", gap: 8 }}>
              <Select className="fill" value={fKind}
                onChange={(e) => { setFKind(e.target.value); pickModel(""); }}>
                <option value="">{fotaKinds.length ? "kind" : "no model declares FOTA"}</option>
                {fotaKinds.map((k) => <option key={k} value={k}>{kindLabel(tpl.data, k)}</option>)}
              </Select>
              <Select className="fill" value={fModelId} disabled={!fKind}
                onChange={(e) => pickModel(e.target.value)}>
                <option value="">{fKind ? "model" : "kind first"}</option>
                {kindModels.map((m) => <option key={m.version_id} value={m.version_id}>{m.name} · {m.manufacturer}</option>)}
              </Select>
            </div>
          </Field>
          <Field label="4 · Node (what to update)">
            <Select className="fill" value={fNode} disabled={!fModelId}
              onChange={(e) => pickNode(e.target.value)}>
              <option value="">{fModelId ? "choose" : "model first"}</option>
              {nodes.map((n) => (
                <option key={n} value={n}>{NODE_LABEL[n] ?? n}</option>
              ))}
            </Select>
          </Field>
          <Field label="5 · Software in that node">
            {/* ONE OPTION PER DRIVABLE METHOD OF THE NODE. Today that is the
                node's app image: every legacy method is an `app` artefact on a
                FOTA route, so a bootloader or a config -- publishable from the
                upload form -- has no engine and cannot appear here. */}
            <Select className="fill" value={fMethod} disabled={!fNode}
              onChange={(e) => { setFMethod(e.target.value); setFwId(""); setSelIds([]); }}>
              <option value="">{fNode ? "select…" : "node first"}</option>
              {nodeMethods(fNode).map((m) => (
                <option key={m.key} value={m.key}>
                  Application SW - via {m.route}{m.supported_now ? "" : " (no engine yet)"}
                </option>
              ))}
            </Select>
            {curMethod?.path && (
              <div className="mono" style={{ fontSize: 11, color: "var(--t-dim)", marginTop: 4 }}>{curMethod.path}</div>
            )}
            {curMethod && !methodReady && (
              <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 4 }}>
                Recorded in the batch, but Orbit has no rollout engine for this software line yet - launch is disabled.
              </div>
            )}
          </Field>
          <Field label="6 · Target firmware (what they get)">
            <Select className="fill" value={fwId} disabled={!fMethod}
              onChange={(e) => setFwId(e.target.value)}>
              <option value="">{fMethod ? "select…" : "software first"}</option>
              {/* FILTERED BY (node, artefact), not by the legacy `software`
                  mirror, which is NULL for everything that is not an app --
                  so a bootloader row would have matched `undefined` and shown
                  up in a list of things that can be rolled out. */}
              {firmwares.filter((f) => (f.node ?? f.software) === selSoftware
                && (f.artefact ?? "app") === "app"
                && (!f.version_id || f.version_id === fModelId)
                && (!f.companies.length || f.companies.includes(fCompany))).map((f) => (
                <option key={f.id} value={f.id}>v{f.version} · {f.filename ?? f.product_id}</option>))}
            </Select>
          </Field>
          <Field label="7 · Boot version (current)">
            <VerMultiSelect options={meta?.bl_vers ?? []} value={fBl}
              onChange={(v) => { setFBl(v); setSelIds([]); }} />
          </Field>
          <Field label="8 · App version (current)">
            <VerMultiSelect options={meta?.app_vers ?? []} value={fApp}
              onChange={(v) => { setFApp(v); setSelIds([]); }} />
          </Field>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
              {!fCompany
                ? "Choose one company in the top bar to see devices"
                : <>{knownDevices.length} shown · <b style={{ color: "var(--t-bright)" }}>{selIds.length} selected</b></>}
            </span>
            {selIds.length > 0 && (
              <button className="btn ghost" onClick={() => setSelIds([])}>Clear</button>
            )}
          </div>
          <div className="tblwrap" style={{ maxHeight: 240, overflowY: "auto" }}>
            <table className="tbl">
              <thead><tr>
                <th style={{ width: 30 }}>
                  <input type="checkbox"
                    checked={knownDevices.length > 0 && knownDevices.every((d) => selIds.includes(d.imei))}
                    onChange={(e) => {
                      const shown = knownDevices.map((d) => d.imei);
                      setSelIds(e.target.checked
                        ? [...new Set([...selIds, ...shown])]
                        : selIds.filter((id) => !shown.includes(id)));
                    }}
                    title="select all shown (filtered)" />
                </th>
                {/* Dattatray: the picker needs "board serial number, asset id
                    if installed, BLE/other controller SW versions". An IMEI
                    names the MODEM; the person choosing units is holding
                    boards and reading a work order, and neither of those is
                    an IMEI. `Planes` carries the non-host controllers
                    unconditionally, so their versions no longer require
                    selecting that software line first; the column beside it
                    stays the TARGET version, which is what the rollout
                    changes. */}
                <th>IMEI</th><th>Board serial</th><th>Asset</th><th>Company</th><th>Boot</th><th>App</th>{selSoftware !== "host" && <th>{selSoftware === "modem_cell" ? "Modem SW" : "BLE SW"}</th>}<th>Planes</th><th>Mode</th>
              </tr></thead>
              <tbody>
                {knownDevices.map((d) => (
                  <tr key={d.imei} style={{ cursor: "pointer" }}
                    onClick={() => setSelIds(selIds.includes(d.imei) ? selIds.filter((x) => x !== d.imei) : [...selIds, d.imei])}>
                    <td><input type="checkbox" readOnly checked={selIds.includes(d.imei)} /></td>
                    <td className="mono">{d.imei}</td>
                    {/* NOT "unknown": no open part binding means the board has
                        not been enrolled against this modem yet, which is the
                        normal state before EOL rather than missing data. */}
                    <td className="mono" title={d.serial_no ?? "no board enrolled against this modem"}>
                      {d.serial_no ?? <span style={{ color: "var(--t-dim)" }}>—</span>}</td>
                    <td className="mono" title={d.asset_id ?? "not fitted to an asset"}>
                      {d.asset_id ?? <span style={{ color: "var(--t-dim)" }}>—</span>}</td>
                    <td>{d.company ?? <span style={{ color: "var(--t-dim)" }}>—</span>}</td>
                    <td>{d.bl_ver != null ? `v${d.bl_ver}` : "—"}</td>
                    <td>{d.app_ver != null ? `v${d.app_ver}` : "—"}</td>{selSoftware !== "host" && <td className="mono">{(selSoftware === "modem_cell" ? d.plane_vers?.modem_cell?.sw : d.plane_vers?.ble?.sw) ?? "—"}</td>}
                    <td className="mono" style={{ fontSize: 11.5 }}>{planeSummary(d.plane_vers)}</td>
                    <td>{d.mode ?? "—"}</td>
                  </tr>
                ))}
                {/* colSpan below is COUNTED, not guessed: checkbox, IMEI,
                    serial, asset, company, boot, app, [target SW], planes,
                    mode. The old value was 7 against 6 actual columns --
                    harmless because browsers clamp it, and wrong, which is how
                    it survived two column additions unnoticed. */}
                {knownDevices.length === 0 && (
                  <tr><td colSpan={selSoftware !== "host" ? 10 : 9} style={{ padding: 12, color: "var(--t-dim)", fontSize: 12 }}>
                    {fCompany ? "No devices match these filters."
                              : "Pick an owner in step 1 to list its devices."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <ActionRow>
          {createMsg && (
            <span style={{ fontSize: 12, color: createMsg.ok ? "var(--ok)" : "var(--bad)", maxWidth: 420, alignSelf: "center" }}>
              {createMsg.text}
            </span>
          )}
          {/* BUSY ELSEWHERE, said out loud. A device a live rollout still
              holds is excluded from this one, so showing only the matched
              count would promise devices this batch will not touch. */}
          {heldElsewhere > 0 && (
            <span style={{ fontSize: 12, color: "var(--warn)", alignSelf: "center" }}>
              {heldElsewhere} of {total} already in a running rollout - they
              rejoin when that one succeeds, is cancelled, or expires
            </span>
          )}
          <Field label="Rollout name">
            <SearchInput style={{ width: 220, minWidth: 0 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. cluster v12 pilot" />
          </Field>
          {/* THE COMMAND'S LIFETIME. There is no "forever": unreachable
              devices would hold their place in this rollout and be skipped by
              every later one, with nothing on screen saying why (0078). */}
          <Field label="Ends after (hours)">
            <SearchInput style={{ width: 110, minWidth: 0 }} value={lifetimeH}
              onChange={(e) => setLifetimeH(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="24" />
          </Field>
          <button className="btn teal" style={{ opacity: fwId && (selIds.length || total > 0) ? 1 : 0.55 }}
            disabled={!fCompany || !methodReady || !fwId || (!selIds.length && total === 0) || create.isPending}
            onClick={() => {
              if (selIds.length) { create.mutate(); return; }
              ask(<>Create a rollout for <b>{targetable}</b> device{targetable === 1 ? "" : "s"}?
                {heldElsewhere > 0 && <> {total} match the filters and {heldElsewhere} are
                already in a running rollout, so those are left out.</>}
                {" "}No devices are ticked, so the batch covers the whole filtered set, snapshotted now.
                It ends after {lifetimeH || "24"} hour{lifetimeH === "1" ? "" : "s"}.</>,
                () => create.mutate(), "Create rollout");
            }}
            title={!fModelId ? "pick a subcomponent model first"
              : !curMethod ? "pick an update method"
              : !methodReady ? "Orbit cannot drive this software line yet" : !fwId ? "pick a target firmware"
              : selIds.length ? `${selIds.length} hand-picked device${selIds.length === 1 ? "" : "s"}`
              : heldElsewhere > 0
                ? `covers ${targetable} of ${total} matching - ${heldElsewhere} are in another rollout`
                : `no devices ticked - covers ALL ${total} matching the filters`}>
            {selIds.length ? `Create rollout (${selIds.length})` : `Create rollout (${targetable})`}
          </button>
        </ActionRow>
      </div>

      <CollapsibleSection title="Active rollouts" count={activeList.length} defaultOpen>
        {activeList.map((b) => (
          <BatchCard key={b.id} b={b}
            onStart={() => start.mutate(b.id)} onCancel={() => cancel.mutate(b.id)}
            onArchive={() => archive.mutate(b.id)}
            onDelete={() => ask(`Delete draft rollout “${b.name}”?`, () => del.mutate(b.id), "Delete")} />
        ))}
        {activeList.length === 0 && <div style={{ padding: 12, color: "var(--t-dim)", fontSize: 12 }}>no active rollouts</div>}
      </CollapsibleSection>

      {dialog}
      {archivedList.length > 0 && (
        <CollapsibleSection title="Archived rollouts" count={archivedList.length}>
          {archivedList.map((b) => (
            <BatchCard key={b.id} b={b}
              onStart={() => start.mutate(b.id)} onCancel={() => cancel.mutate(b.id)}
              onArchive={() => archive.mutate(b.id)}
              onDelete={() => ask(`Delete archived rollout “${b.name}”? This permanently removes it and its device records.`, () => del.mutate(b.id), "Delete")} />
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
}

function BatchCard({ b, onStart, onCancel, onArchive, onDelete }: {
  b: Batch; onStart: () => void; onCancel: () => void; onArchive: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ["fota", "batch", b.id],
    queryFn: () => fota.batch(b.id),
    enabled: open || b.status === "active",
    // 4s, not 1.5s: at fleet scale this payload is counts + up to 500 device
    // rows — a 1.5s cadence would re-render a big table for the whole rollout
    // (the Live Window lesson). Rollout state moves on multi-second timescales.
    refetchInterval: b.status === "active" ? POLL.STATE : false,
  });
  const c = detail.data?.counts ?? {};

  return (
    <div className="panel panel-pad" style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <b style={{ color: "var(--t-bright)", fontSize: 13 }}>{b.name}</b>
        <span style={{ fontSize: 11.5, color: "var(--t-mute)" }}>
          → <b style={{ color: "var(--t-bright)" }}>v{b.target_version}</b> · {b.total} device{b.total === 1 ? "" : "s"}
          {b.wave_size ? ` · wave ${b.wave_size}` : ""}
        </span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn ghost" onClick={() => setOpen((s) => !s)}>{open ? "▾" : "▸"} Devices</button>
          <Chip tone={BATCH_CHIP[b.status] ?? "mute"}>{b.status.toUpperCase()}</Chip>
          {b.status === "draft" && <button className="btn teal" onClick={onStart}><Icon name="play" /> Start</button>}
          {/* CANCEL, not Stop: it is final - there is no resume (D-16) - and
              "Stop" invited one. It releases every device the rollout still
              holds, so they can join another one. */}
          {b.status === "active" && <button className="btn red" onClick={onCancel}><Icon name="stop" /> Cancel</button>}
          {(b.status === "cancelled" || b.status === "expired" || b.status === "done") && !b.archived_at &&
            <button className="btn red" onClick={onArchive} title="move out of the active list">Archive</button>}
          {(b.status === "draft" || b.archived_at) &&
            <button className="btn red" onClick={onDelete}
              title={b.archived_at ? "delete this archived rollout" : "delete this never-started draft"}>
              <Icon name="delete" /> Delete
            </button>}
        </span>
      </div>

      {/* slices = the EXACT rollout states, same labels/colors as the chips */}
      <Donut
        slices={STATE_ORDER
          .filter((st) => (c[st] ?? 0) > 0)
          .map((st) => ({ label: STATE_CHIP[st].label, value: c[st] ?? 0, color: STATE_CHIP[st].color }))}
      />

      {/* per-state device breakdown - exact count in each rollout state */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
        {STATE_ORDER.filter((s) => (c[s] ?? 0) > 0).map((s) => (
          <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <StatusChip st={s} map={STATE_CHIP} /><b style={{ fontSize: 11.5, color: "var(--t-bright)" }}>{c[s]}</b>
          </span>
        ))}
        {Object.values(c).every((n) => !n) && (
          <span style={{ fontSize: 12, color: "var(--t-mute)" }}>No device state yet.</span>
        )}
      </div>

      {open && detail.data && (
        <div style={{ marginTop: 10 }}>
          <DataTable rows={detail.data.devices} cols={BATCH_COLS} rowKey={(d) => d.connection_id}
            maxHeight={420}
            empty={<div className="panel panel-pad cap">No devices in this rollout.</div>} />
        </div>
      )}
    </div>
  );
}

/** Rollout device columns — module-const = identity-stable, so DataTable's
 *  memoized rows re-render only the devices whose state actually moved. */
const BATCH_COLS: DataCol<BatchDevice>[] = [
  { key: "dev", priority: 1, label: "Connection ID", width: 150, mono: true, render: (d) => <span className="dev">{d.connection_id}</span> },
  { key: "state", priority: 1, label: "State", width: 150, render: (d) => <StatusChip st={d.state} map={STATE_CHIP} /> },
  { key: "stage", priority: 2, label: "Stage", width: 110, render: (d) => <>st {d.last_st ?? "—"}{d.last_err ? ` · err ${d.last_err}` : ""}</> },
  { key: "ver", priority: 2, label: "Version", width: 70, render: (d) => d.current_app_ver ?? "—" },
  { key: "mode", priority: 3, label: "Mode", width: 70, render: (d) => d.mode ?? "—" },
  { key: "safe", priority: 3, label: "Safe", width: 60, render: (d) => (d.mode === "app" ? (d.fota_allowed ? "yes" : "no") : "—") },
  { key: "att", priority: 3, label: "Attempts", width: 80, render: (d) => d.attempts },
  { key: "err", priority: 2, label: "Errors", width: 70,
    render: (d) => (d.error_count ? <span style={{ color: "#E64980" }}>{d.error_count}/3</span> : "—") },
];
